// Device props for the OMG-2313 card-form micro-funnel events.
//
// Mixpanel's own $os/$device_type arrive via Segment's device enrichment, but
// they were EMPTY on the checkout funnel events this was built to analyse
// (OMG-2278: the iOS-vs-desktop split had to be inferred from $browser alone,
// which conflates Mobile Safari with iOS Chrome). Sending our own props keeps
// the micro-funnel sliceable by platform regardless of that enrichment.
//
// Scoped deliberately to the new events: adding props to the existing funnel
// events mid-experiment would change their shape while the A/B test is running.

/**
 * Coarse OS bucket. `isTouchMac` carries the one thing the UA string cannot
 * say for itself: iPadOS 13+ requests desktop sites by default, so its UA is
 * byte-identical to a Mac's — no `iPad`, no `Mobile` token — and touch points
 * are the only signal left. Without it an iPad reports `macOS` /
 * `is_mobile: false`, which silently dilutes both sides of the platform split
 * these props exist to measure.
 */
const osFromUserAgent = (ua: string, isTouchMac: boolean): string => {
  if (/iPhone|iPad|iPod/.test(ua) || isTouchMac) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Linux/.test(ua)) return "Linux";
  return "other";
};

/**
 * `{ os, is_mobile }` for analytics props. Returns an empty object outside a
 * browser so a caller can spread it unconditionally.
 */
export const deviceContext = (): { os?: string; is_mobile?: boolean } => {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent ?? "";
  // Same test as `getIsIOS` (VideoReactPlayer), inlined rather than imported:
  // that one memoizes its answer for the page lifetime, which is right for a
  // player deciding a load strategy once and wrong for a per-event prop.
  const isTouchMac =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const os = osFromUserAgent(ua, isTouchMac);
  return {
    os,
    // Phones and tablets both count: the OMG-2278 gap is a touch-form problem,
    // not a screen-size one.
    is_mobile: os === "iOS" || os === "Android" || /Mobile/.test(ua),
  };
};
